import { GlButton, GlCollapse, GlIcon, GlLink } from '@gitlab/ui';
import { translate } from '../../../../../utils/i18n';
import MessageToolApproval from '../../duo_chat_message_tool_approval/message_tool_approval';
import { APPROVAL_TOOL_NAMES } from '../../../constants';
import MessageToolDetails from './message_tool_details';
import BaseMessage from './message_base';
import __vue_normalize__ from 'vue-runtime-helpers/dist/normalize-component.js';

const i18n = {
  PROJECT_LABEL: translate('DuoChatMessage.projectLabel', 'Project'),
  BRANCH_LABEL: translate('DuoChatMessage.branchLabel', 'Branch'),
  START_BRANCH_LABEL: translate('DuoChatMessage.startBranchLabel', 'Start branch'),
  COLLAPSE_DETAILS: translate('DuoChatMessage.collapseDetails', 'Collapse details'),
  EXPAND_DETAILS: translate('DuoChatMessage.expandDetails', 'Expand details')
};

// Hide details section for specified subtypes
const hiddenSubTypes = ['command_output'];
var script = {
  name: 'DuoToolMessage',
  components: {
    BaseMessage,
    MessageToolApproval,
    MessageToolDetails,
    GlButton,
    GlCollapse,
    GlIcon,
    GlLink
  },
  i18n,
  provide: {
    renderGFM: {
      from: 'renderGFM',
      default: () => element => {
        element.classList.add('gl-markdown', 'gl-compact-markdown');
        element.classList.add('duo-system-message', 'gl-text-sm');
      }
    }
  },
  inject: {
    detailsExpanded: {
      default: false
    }
  },
  props: {
    message: {
      required: true,
      type: Object,
      validate: value => {
        return value && value.tool_info;
      }
    },
    workingDirectory: {
      required: false,
      type: String,
      default: ''
    }
  },
  data() {
    return {
      isDetailsOpen: this.detailsExpanded
    };
  },
  computed: {
    iconName() {
      switch (this.toolName) {
        case 'read_file':
          return 'eye';
        case 'write_file':
          return 'pencil';
        case 'grep_files':
        case 'list_files':
          return 'search';
        case 'get_issue':
        case 'list_issue_notes':
          return 'issues';
        case 'create_commit':
          return 'commit';
        default:
          return 'issue-type-maintenance';
      }
    },
    messageContent() {
      var _this$message$content;
      return ((_this$message$content = this.message.content) === null || _this$message$content === void 0 ? void 0 : _this$message$content.toLowerCase()) || '';
    },
    messageFilePath() {
      var _this$message$tool_in, _this$message$tool_in2;
      return (_this$message$tool_in = this.message.tool_info) === null || _this$message$tool_in === void 0 ? void 0 : (_this$message$tool_in2 = _this$message$tool_in.args) === null || _this$message$tool_in2 === void 0 ? void 0 : _this$message$tool_in2.file_path;
    },
    toolArgs() {
      var _this$message$tool_in3;
      return ((_this$message$tool_in3 = this.message.tool_info) === null || _this$message$tool_in3 === void 0 ? void 0 : _this$message$tool_in3.args) || {};
    },
    projectId() {
      return this.toolArgs.project_id;
    },
    branchName() {
      return this.toolArgs.branch;
    },
    startBranch() {
      return this.toolArgs.start_branch;
    },
    shouldShowProjectId() {
      if (!this.projectId) return false;
      // Hide if content already mentions the project ID
      return !this.messageContent.includes(String(this.projectId));
    },
    shouldShowBranch() {
      if (!this.branchName) return false;
      // Hide if content already mentions the branch name
      return !this.messageContent.includes(this.branchName.toLowerCase());
    },
    shouldShowStartBranch() {
      if (!this.startBranch) return false;
      // Hide if content already mentions the start branch
      return !this.messageContent.includes(this.startBranch.toLowerCase());
    },
    shouldShowFilePath() {
      if (!this.messageFilePath) return false;
      // Hide if content already mentions the file path
      return !this.messageContent.includes(this.messageFilePath.toLowerCase());
    },
    hasMetadataToShow() {
      return this.shouldShowProjectId || this.shouldShowBranch || this.shouldShowStartBranch;
    },
    hasCommandOutput() {
      return this.message.message_sub_type === 'command_output';
    },
    hasToolInfoArgs() {
      return Object.keys(this.toolArgs).length > 0;
    },
    hasToolInfoResponse() {
      return Object.prototype.hasOwnProperty.call(this.message.tool_info || {}, 'tool_response');
    },
    toolName() {
      var _this$message$tool_in4;
      return (_this$message$tool_in4 = this.message.tool_info) === null || _this$message$tool_in4 === void 0 ? void 0 : _this$message$tool_in4.name;
    },
    toolResponseContent() {
      var _this$message$tool_in5, _this$message$tool_in6;
      return (_this$message$tool_in5 = this.message.tool_info) === null || _this$message$tool_in5 === void 0 ? void 0 : (_this$message$tool_in6 = _this$message$tool_in5.tool_response) === null || _this$message$tool_in6 === void 0 ? void 0 : _this$message$tool_in6.content;
    },
    collapseIconName() {
      return this.isDetailsOpen ? 'chevron-down' : 'chevron-right';
    },
    requiresApproval() {
      return Object.values(APPROVAL_TOOL_NAMES).includes(this.toolName);
    },
    shouldShowDetails() {
      var _this$message;
      return !hiddenSubTypes.includes((_this$message = this.message) === null || _this$message === void 0 ? void 0 : _this$message.message_sub_type) && (this.hasToolInfoArgs || this.hasToolInfoResponse);
    }
  },
  methods: {
    onOpenFilePath(filePath) {
      const pathToEmit = filePath || this.messageFilePath;
      if (pathToEmit) {
        this.$emit('open-file-path', pathToEmit);
      }
    },
    toggleDetails() {
      if (!this.shouldShowDetails) return;
      this.isDetailsOpen = !this.isDetailsOpen;
    },
    onCopyCodeSnippet(e) {
      /**
       * Emit copy-code-snippet event that clients can use to interact with a suggested code.
       * @param {*} event An event containing code string in the "detail.code" field.
       */
      this.$emit('copy-code-snippet', e);
    }
  }
};

/* script */
const __vue_script__ = script;

/* template */
var __vue_render__ = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return (_vm.requiresApproval)?_c('message-tool-approval',{attrs:{"message":_vm.message,"working-directory":_vm.workingDirectory,"approval-status":"approved"}}):_c('base-message',{attrs:{"message":_vm.message},scopedSlots:_vm._u([{key:"message",fn:function(ref){
var content = ref.content;
return [_c('div',{staticClass:"gl-border gl-flex-col gl-rounded-lg gl-border-default gl-p-4"},[_c('div',{staticClass:"gl-flex gl-flex-col gl-gap-2 gl-text-base gl-text-subtle"},[_c('div',{staticClass:"gl-flex gl-items-start gl-gap-x-3"},[_c('div',{staticClass:"gl-mt-1 gl-flex gl-flex-shrink-0"},[_c('gl-icon',{attrs:{"name":_vm.iconName}})],1),_vm._v(" "),_c('div',{staticClass:"gl-flex gl-min-w-0 gl-grow gl-flex-wrap gl-items-center gl-gap-x-2"},[_c('span',{attrs:{"data-testid":"tool-message-content"}},[_vm._v(_vm._s(content))]),_vm._v(" "),(_vm.shouldShowFilePath)?_c('gl-link',{staticClass:"file-path-link gl-min-w-0 gl-break-all gl-font-monospace",attrs:{"data-testid":"tool-message-file-path-link"},on:{"click":function($event){$event.preventDefault();return _vm.onOpenFilePath(_vm.messageFilePath)}}},[_c('code',{staticClass:"gl-rounded-base gl-px-2 gl-py-1 gl-text-default"},[_vm._v(_vm._s(_vm.messageFilePath))])]):_vm._e()],1),_vm._v(" "),(_vm.shouldShowDetails)?_c('gl-button',{staticClass:"-gl-mt-1",attrs:{"category":"tertiary","size":"small","icon":_vm.collapseIconName,"aria-label":_vm.isDetailsOpen ? _vm.$options.i18n.COLLAPSE_DETAILS : _vm.$options.i18n.EXPAND_DETAILS,"data-testid":"tool-message-toggle-button"},on:{"click":_vm.toggleDetails}}):_vm._e()],1),_vm._v(" "),(_vm.hasMetadataToShow)?_c('div',{staticClass:"gl-ml-5 gl-flex gl-flex-wrap gl-gap-2 gl-text-sm"},[(_vm.shouldShowProjectId)?_c('div',{staticClass:"gl-flex gl-min-w-0 gl-gap-x-2 gl-rounded-full gl-bg-strong gl-px-3",attrs:{"data-testid":"tool-message-project-info"}},[_c('span',{staticClass:"gl-whitespace-nowrap"},[_vm._v(_vm._s(_vm.$options.i18n.PROJECT_LABEL)+":")]),_vm._v(" "),_c('span',{staticClass:"gl-truncate",attrs:{"data-testid":"tool-message-project-id"}},[_vm._v(_vm._s(_vm.projectId))])]):_vm._e(),_vm._v(" "),(_vm.shouldShowBranch)?_c('div',{staticClass:"gl-flex gl-min-w-0 gl-gap-x-2 gl-rounded-full gl-bg-strong gl-px-3",attrs:{"data-testid":"tool-message-branch-info"}},[_c('span',{staticClass:"gl-whitespace-nowrap"},[_vm._v(_vm._s(_vm.$options.i18n.BRANCH_LABEL)+":")]),_vm._v(" "),_c('span',{staticClass:"gl-truncate",attrs:{"data-testid":"tool-message-branch-name"}},[_vm._v(_vm._s(_vm.branchName))])]):_vm._e(),_vm._v(" "),(_vm.shouldShowStartBranch)?_c('div',{staticClass:"gl-flex gl-min-w-0 gl-gap-x-2 gl-rounded-full gl-bg-strong gl-px-3",attrs:{"data-testid":"tool-message-start-branch-info"}},[_c('span',{staticClass:"gl-whitespace-nowrap"},[_vm._v(_vm._s(_vm.$options.i18n.START_BRANCH_LABEL)+":")]),_vm._v(" "),_c('span',{staticClass:"gl-truncate",attrs:{"data-testid":"tool-message-start-branch-name"}},[_vm._v(_vm._s(_vm.startBranch))])]):_vm._e()]):_vm._e()]),_vm._v(" "),(_vm.shouldShowDetails)?_c('gl-collapse',{staticClass:"gl-overflow-hidden",attrs:{"visible":_vm.isDetailsOpen}},[_c('message-tool-details',{attrs:{"message":_vm.message,"is-expanded":_vm.isDetailsOpen},on:{"copy-code-snippet":_vm.onCopyCodeSnippet}})],1):_vm._e()],1)]}}])})};
var __vue_staticRenderFns__ = [];

  /* style */
  const __vue_inject_styles__ = undefined;
  /* scoped */
  const __vue_scope_id__ = undefined;
  /* module identifier */
  const __vue_module_identifier__ = undefined;
  /* functional template */
  const __vue_is_functional_template__ = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = __vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  );

export default __vue_component__;
export { i18n };
